/**
 * Timezone Selector Component
 *
 * Auto-detects user's browser timezone with manual override option.
 * Displays current time in selected timezone for user confirmation.
 */

import { formatInTimeZone } from "date-fns-tz";
import { useEffect, useId, useState } from "react";
import { Clock9 } from "@/components/animate-ui/icons/clock-9";
import { Label } from "@/components/ui/label";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

// Common timezones for the dropdown
// Organized by region for better UX
const COMMON_TIMEZONES = [
	// UTC
	{ value: "UTC", label: "UTC (Coordinated Universal Time)" },

	// Americas
	{ value: "America/New_York", label: "Eastern Time (New York)" },
	{ value: "America/Chicago", label: "Central Time (Chicago)" },
	{ value: "America/Denver", label: "Mountain Time (Denver)" },
	{
		value: "America/Los_Angeles",
		label: "Pacific Time (San Francisco/Los Angeles)",
	},
	{ value: "America/Anchorage", label: "Alaska Time (Anchorage)" },
	{ value: "Pacific/Honolulu", label: "Hawaii Time (Honolulu)" },
	{ value: "America/Toronto", label: "Eastern Time (Toronto)" },
	{ value: "America/Mexico_City", label: "Central Time (Mexico City)" },
	{ value: "America/Sao_Paulo", label: "Brasilia Time" },
	{ value: "America/Argentina/Buenos_Aires", label: "Argentina Time" },

	// Europe
	{ value: "Europe/London", label: "GMT/BST (London)" },
	{ value: "Europe/Paris", label: "Central European Time (Paris)" },
	{ value: "Europe/Berlin", label: "Central European Time (Berlin)" },
	{ value: "Europe/Rome", label: "Central European Time (Rome)" },
	{ value: "Europe/Madrid", label: "Central European Time (Madrid)" },
	{ value: "Europe/Amsterdam", label: "Central European Time (Amsterdam)" },
	{ value: "Europe/Stockholm", label: "Central European Time (Stockholm)" },
	{ value: "Europe/Moscow", label: "Moscow Time" },
	{ value: "Europe/Istanbul", label: "Turkey Time" },

	// Asia
	{ value: "Asia/Dubai", label: "Gulf Standard Time (Dubai)" },
	{ value: "Asia/Kolkata", label: "India Standard Time" },
	{ value: "Asia/Shanghai", label: "China Standard Time" },
	{ value: "Asia/Tokyo", label: "Japan Standard Time" },
	{ value: "Asia/Seoul", label: "Korea Standard Time" },
	{ value: "Asia/Singapore", label: "Singapore Time" },
	{ value: "Asia/Hong_Kong", label: "Hong Kong Time" },
	{ value: "Asia/Bangkok", label: "Indochina Time" },

	// Pacific
	{ value: "Australia/Sydney", label: "Australian Eastern Time (Sydney)" },
	{
		value: "Australia/Melbourne",
		label: "Australian Eastern Time (Melbourne)",
	},
	{ value: "Australia/Perth", label: "Australian Western Time" },
	{ value: "Pacific/Auckland", label: "New Zealand Time" },

	// Africa
	{ value: "Africa/Cairo", label: "Eastern European Time (Cairo)" },
	{ value: "Africa/Johannesburg", label: "South Africa Time" },
	{ value: "Africa/Lagos", label: "West Africa Time" },
] as const;

interface TimezoneSelectorProps {
	value: string;
	onChange: (timezone: string) => void;
	label?: string;
	showCurrentTime?: boolean;
}

export function TimezoneSelector({
	value,
	onChange,
	label = "Timezone",
	showCurrentTime = true,
}: TimezoneSelectorProps) {
	const id = useId();
	const [currentTime, setCurrentTime] = useState<string>("");
	const [hasAutoDetected, setHasAutoDetected] = useState(false);

	// Auto-detect browser timezone on mount
	useEffect(() => {
		// Only auto-detect once, and only if value is unset or default UTC
		if (!hasAutoDetected && (!value || value === "UTC")) {
			try {
				const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
				if (detected) {
					onChange(detected);
					setHasAutoDetected(true);
				}
			} catch (error) {
				console.warn("Failed to detect timezone:", error);
				// Fallback to America/Los_Angeles (PST/PDT)
				onChange("America/Los_Angeles");
				setHasAutoDetected(true);
			}
		}
	}, [value, onChange, hasAutoDetected]); // Run when value or hasAutoDetected changes

	// Update current time every minute
	useEffect(() => {
		const updateTime = () => {
			try {
				const now = new Date();
				const formatted = formatInTimeZone(now, value, "h:mm a");
				setCurrentTime(formatted);
			} catch (error) {
				console.warn("Failed to format time:", error);
				setCurrentTime("");
			}
		};

		updateTime(); // Initial update
		const interval = setInterval(updateTime, 60000); // Update every minute

		return () => clearInterval(interval);
	}, [value]);

	// Check if current timezone is in the common list
	const isCommonTimezone = COMMON_TIMEZONES.some((tz) => tz.value === value);

	return (
		<div className="space-y-2">
			<Label htmlFor={id}>{label}</Label>
			<Select value={value} onValueChange={onChange}>
				<SelectTrigger id={id} className="w-full">
					<SelectValue placeholder="Select timezone" />
				</SelectTrigger>
				<SelectContent>
					{/* Show current value if not in common list */}
					{!isCommonTimezone && value && <SelectItem value={value}>{value} (Current)</SelectItem>}

					{/* Common timezones */}
					{COMMON_TIMEZONES.map((tz) => (
						<SelectItem key={tz.value} value={tz.value}>
							{tz.label}
						</SelectItem>
					))}
				</SelectContent>
			</Select>

			{/* Current time display */}
			{showCurrentTime && currentTime && (
				<div className="flex items-center gap-2 text-sm text-muted-foreground">
					<Clock9 animateOnHover className="size-4" />
					<span>
						Current time: <span className="font-medium text-foreground">{currentTime}</span>
					</span>
				</div>
			)}
		</div>
	);
}
